import { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Upload, FileText, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`);

const openFile = async (f) => {
  const win = window.open("", "_blank");
  try {
    const { data } = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (win) win.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    toast.error("Could not open file");
  }
};

const FileRow = ({ f, canDelete, onDelete }) => (
  <li className="flex items-center gap-2 px-4 py-2.5" data-testid={`attachment-${f.id}`}>
    <FileText className="h-4 w-4 shrink-0 text-sky-700" />
    <div className="min-w-0 flex-1">
      <button onClick={() => openFile(f)} className="block max-w-full truncate text-left text-sm font-medium text-slate-900 hover:text-sky-700" data-testid={`btn-open-attachment-${f.id}`}>
        {f.original_filename}<ExternalLink className="ml-1 inline h-3 w-3" />
      </button>
      <p className="text-[11px] text-slate-500">{f.category} · {kb(f.size)} · {f.uploaded_by} · {fmtDate(f.created_at)}</p>
    </div>
    {canDelete && <Button size="icon" variant="ghost" onClick={() => onDelete(f)} data-testid={`btn-delete-attachment-${f.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
  </li>
);

export const Attachments = ({ entityType, entityId, writable, canDelete = writable, defaultCategory = "Test Certificate", onChange }) => {
  const [files, setFiles] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(defaultCategory);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const load = useCallback(() => api.get("/files", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => setFiles(r.data)), [entityType, entityId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/files/categories").then((r) => setCats(r.data)); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB"); return; }
    const fd = new FormData();
    fd.append("entity_type", entityType);
    fd.append("entity_id", entityId);
    fd.append("category", cat);
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/files", fd); toast.success(`${file.name} uploaded`); load(); onChange?.(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const del = async (f) => {
    try { await api.delete(`/files/${f.id}`); toast.success("Attachment removed"); load(); onChange?.(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="attachments-panel">
      {writable && (
        <div className="flex flex-col gap-2 border-b border-slate-200 p-3 sm:flex-row">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="sm:w-52" data-testid="select-attachment-category"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={input} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={upload} data-testid="input-attachment-file" />
          <Button variant="outline" onClick={() => input.current?.click()} disabled={busy} data-testid="btn-upload-attachment">
            <Upload className="mr-1.5 h-4 w-4" />{busy ? "Uploading..." : "Upload file"}
          </Button>
        </div>
      )}
      <ul className="divide-y divide-slate-100">
        {files.map((f) => <FileRow key={f.id} f={f} canDelete={canDelete} onDelete={del} />)}
        {!files.length && <li className="flex items-center gap-2 px-4 py-6 text-sm text-slate-500" data-testid="attachments-empty"><Paperclip className="h-4 w-4" />No attachments yet</li>}
      </ul>
      {writable && <p className="px-4 pb-3 text-[11px] text-slate-400">PDF, PNG, JPG or WEBP · max 10 MB</p>}
    </div>
  );
};
